import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import type { TagManagerArgs } from "react-gtm-module";
import TagManager from "react-gtm-module";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";

import { getApp, initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

import * as Sentry from "@sentry/react";

import i18n from "lang";

import { isSpeculativeCalendarPreloadError, startCalendarPreload } from "utils/calendarRoute";
import { shouldReloadAfterPreloadError } from "utils/preloadErrorReload";
import { buildSentryOptions } from "utils/sentryOptions";

import App from "./App";

import { store } from "./store/store";

/*
  FIREBASE
*/

const initializeFirebase = () => {
  try {
    getApp();
  } catch (error) {
    const firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };

    const app = initializeApp(firebaseConfig);
    getStorage(app);
    getFirestore(app); // Firestore pro přímé ukládání FCM tokenů (push notifikace)
  }
};

initializeFirebase();

/*
  GTM
*/

if (import.meta.env.VITE_ENV === "production") {
  const tagManagerArgs: TagManagerArgs = {
    gtmId: "GTM-MV9PHVQP",
    auth: import.meta.env.VITE_GTM_AUTH,
    preview: import.meta.env.VITE_GTM_PREVIEW,
  };

  TagManager.initialize(tagManagerArgs);
}

/*
  SENTRY
*/

Sentry.init({
  ...buildSentryOptions(import.meta.env),
  // AIDEV-NOTE: `feedbackIntegration` tu bývala registrovaná s `autoInject: false`, tedy jako
  // widget, který se sám nikdy nezobrazí — a `FeedbackForm.tsx` si stejně vyrábí vlastní
  // instanci a mountuje ji přes `createWidget()`. Registrace tady byla čistě navíc a držela
  // `@sentry-internal/feedback` (48 kB) ve vstupním chunku. `replayIntegration` naopak zůstává
  // eager schválně: `replaysOnErrorSampleRate: 1.0` znamená, že se má nahrávat od startu, a
  // líné načtení by zahodilo právě chyby padlé při bootu.
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [import.meta.env.VITE_GRAPHQL_ENDPOINT],
    }),
  ],
});

// AIDEV-NOTE: Bez `preventDefault()` schválně — Vite by pak lazy routě vrátil `undefined` a React
// by místo původní chyby nahlásil matoucí pád `lazy`. Chyba tak dojde do Sentry a stránka se
// mezitím znovu načte v nové verzi. [TD-452]
//
// AIDEV-NOTE: Selhání SPEKULATIVNÍHO přednačtení kalendáře (idle, najetí na odkaz, prefetch) reload
// nespouští: Vite událost vysílá synchronně dřív, než se zamítne promise, takže ji `.catch` na `preload()`
// nezastaví, a stránka by se znovu načetla uprostřed rozpracovaného cvičení kvůli chunku, o který student
// nežádal. Handler proto čte příznak z `calendarRoute` a podle `event.payload` ověří, že selhal právě
// chunk kalendáře (selhání chunku jiné routy reload spustí). Skutečná navigace na /kalendar (a start na něm)
// příznak nenastaví a chová se jako dřív. [TD-476]
window.addEventListener("vite:preloadError", event => {
  // Vite dává chybu do `event.payload` (typ `Event` ho nezná).
  const speculative = isSpeculativeCalendarPreloadError((event as Event & { payload?: unknown }).payload);

  if (shouldReloadAfterPreloadError(Date.now(), { speculative })) {
    window.location.reload();
  }
});

// Start přímo na /kalendar: chunk se stahuje hned, souběžně s dotazem na studenta. Jinde se kalendář
// přednačítá až po načtení studenta (`AuthenticatedArea`). [TD-476]
startCalendarPreload(window.location.pathname);

const rootElement = document.getElementById("root");
if (rootElement === null) {
  throw new Error("Root element not found");
}

// AIDEV-NOTE: Vlastní handler přidává k pádu renderu komponentový stack a kontext `react`, ale nahrazuje
// výchozí výpis Reactu do konzole — proto `console.error` ručně. Bez něj by lokálně (Sentry vypnuté) byla
// bílá obrazovka bez jediné stopy. [TD-427]
const reportUncaughtError = Sentry.reactErrorHandler();
const root = ReactDOM.createRoot(rootElement, {
  onUncaughtError: (error, errorInfo) => {
    reportUncaughtError(error, errorInfo);
    console.error(error, errorInfo.componentStack);
  },
});
root.render(
  <StrictMode>
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <App />
      </I18nextProvider>
    </Provider>
  </StrictMode>,
);
